import Cookies from 'js-cookie';
import qs from 'qs';
import { emitTokenRefreshed } from './tokenEvents';
import {
  getToken,
  getRefreshToken,
  isAccessTokenExpiring,
  cookieOptions,
} from './AuthHandler';

const BASEAPI = process.env.REACT_APP_BASE_API;
const APP_VERSION = process.env.REACT_APP_VERSION || '2.0.0';

// Clase de error personalizada para errores de API
class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// ── Token refresh (understands the {success, data} envelope) ─────────
let isRefreshing = false;
let refreshPromise = null;

const clearAuthCookies = () => {
  Cookies.remove('token', { path: '/' });
  Cookies.remove('refreshToken', { path: '/' });
  Cookies.remove('user_public_id', { path: '/' });
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(BASEAPI + '/user/refresh', {
      method: 'POST',
      headers: { ...baseHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      // Refresh token rejected — the session is really over.
      clearAuthCookies();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/signin')) {
        window.location.href = '/signin';
      }
      return null;
    }

    const json = await res.json();
    // Standard envelope: { success: true, data: { token, refreshToken } }
    // Legacy fallback: { token, refreshToken }
    const payload = json?.data ?? json ?? {};
    const newAccess = payload.token ?? payload.accessToken ?? payload.access_token;
    const newRefresh = payload.refreshToken ?? payload.refresh_token ?? refreshToken;

    // Never write `undefined` into the cookie — that caused logout loops.
    if (!newAccess) return null;

    Cookies.set('token', newAccess, cookieOptions());
    Cookies.set('refreshToken', newRefresh, { ...cookieOptions(), expires: 7 });
    emitTokenRefreshed(newAccess);
    return newAccess;
  } catch {
    return null;
  }
};

const withTokenRefresh = (apiCall) => {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      // Only handle 401 errors for authenticated requests
      if (error instanceof ApiError && error.status === 401) {
        const token = Cookies.get('token');
        if (!token) throw error; // No token, can't refresh

        // Deduplicate concurrent refresh attempts
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken().finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
        }

        const newToken = await refreshPromise;
        if (newToken) {
          // Retry the original request with new token
          return await apiCall(...args);
        }
      }
      throw error;
    }
  };
};

// ── Shared headers (I2: platform + version for the backend) ───────────
const baseHeaders = () => ({
  Accept: 'application/json',
  'X-Platform': 'web',
  'X-App-Version': APP_VERSION,
});

const authHeader = (customToken) => {
  const token = customToken || getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ── Proactive refresh (I1) ────────────────────────────────────────────
const refreshOnce = async () => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshAccessToken().finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/** Refresh the access token shortly before it expires. */
const ensureFreshToken = async () => {
  if (!getToken() || !getRefreshToken()) return;
  if (!isAccessTokenExpiring()) return;
  await refreshOnce();
};

/** Public helper for consumers that are not plain fetch calls (Socket.IO). */
export const getFreshAccessToken = async () => {
  await ensureFreshToken();
  return getToken();
};

// Función auxiliar para manejar respuestas.
// El backend mezcla formatos: { success, <payload> } (categories/ads/states),
// { success, data: <payload> } (trending-ads) y errores en TEXTO PLANO
// (p. ej. 401 "Unauthorized"). Soportamos los tres.
const handleResponse = async (res) => {
  const contentType =
    (res.headers && typeof res.headers.get === 'function' && res.headers.get('content-type')) || '';
  // En tests, `res` no trae headers: se asume JSON por compatibilidad.
  const looksJson = contentType.includes('json') || (!contentType && typeof res.json === 'function');

  let json;
  if (!looksJson) {
    const text = typeof res.text === 'function' ? await res.text().catch(() => '') : '';
    if (!res.ok) {
      throw new ApiError(
        (text && text.slice(0, 200)) || `Request failed (${res.status})`,
        res.status,
        null
      );
    }
    return text;
  }

  try {
    json = await res.json();
  } catch (e) {
    if (!res.ok) {
      throw new ApiError(`Request failed (${res.status})`, res.status, null);
    }
    throw new ApiError('Error parsing response', res.status, null);
  }
  
  // Handle legacy notallowed redirect
  if (json && json.notallowed) {
    window.location.href = '/signin';
    return null;
  }

  // New standardized format: { success: true, data: {...} }
  if (json.success === true) {
    return json.data !== undefined ? json.data : json;
  }

  // New standardized error: { success: false, error: { message: "...", details: {...} } }
  if (json.success === false && json.error) {
    throw new ApiError(json.error.message || 'Request failed', res.status, json.error.details);
  }

  // Legacy error format: { error: {...} }
  if (!res.ok) {
    let errorMsg = json.message;
    if (!errorMsg && typeof json.error === 'object') {
      const msgs = [];
      for (const key of Object.keys(json.error)) {
        if (typeof json.error[key] === 'object' && json.error[key].msg) {
          msgs.push(json.error[key].msg);
        } else if (typeof json.error[key] === 'string') {
          msgs.push(json.error[key]);
        }
      }
      errorMsg = msgs.join(', ') || 'Request failed';
    }
    if (!errorMsg) errorMsg = json.error || 'Request failed';
    throw new ApiError(errorMsg, res.status, json);
  }
  
  // Legacy success: return raw JSON
  return json;
};

const rawFetchGet = async (endpoint, params = {}) => {
  try {
    await ensureFreshToken();
    let url = BASEAPI + endpoint;
    if (Object.keys(params).length > 0) {
      url += '?' + qs.stringify(params);
    }
    const res = await fetch(url, {
      headers: { ...baseHeaders(), ...authHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

const rawFetchPost = async (endpoint, body, customToken = null) => {
  try {
    await ensureFreshToken();
    const headers = {
      ...baseHeaders(),
      'Content-Type': 'application/json',
      ...authHeader(customToken),
    };

    const res = await fetch(BASEAPI + endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

const rawFetchFile = async (endpoint, body, method = 'POST') => {
  try {
    await ensureFreshToken();
    // No Content-Type here: the browser must set the multipart boundary.
    const headers = { ...baseHeaders(), ...authHeader() };

    const res = await fetch(BASEAPI + endpoint, {
      method,
      headers,
      body,
    });

    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

const rawFetchDelete = async (endpoint) => {
  try {
    await ensureFreshToken();
    const res = await fetch(BASEAPI + endpoint, {
      method: 'DELETE',
      headers: { ...baseHeaders(), ...authHeader() },
    });
    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

const rawFetchPatch = async (endpoint, body) => {
  try {
    await ensureFreshToken();
    const headers = {
      ...baseHeaders(),
      'Content-Type': 'application/json',
      ...authHeader(),
    };

    const res = await fetch(BASEAPI + endpoint, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

const rawFetchPut = async (endpoint, body) => {
  try {
    await ensureFreshToken();
    const headers = {
      ...baseHeaders(),
      'Content-Type': 'application/json',
      ...authHeader(),
    };

    const res = await fetch(BASEAPI + endpoint, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });

    return await handleResponse(res);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error: ' + error.message, 0, null);
  }
};

// ── B3: EVERY request goes through the refresh-aware wrappers ─────────
// `apiFetch*` are the public (wrapped) versions used by all the methods
// below, so a 401 anywhere triggers a silent refresh + retry.
const apiFetchGet = withTokenRefresh(rawFetchGet);
const apiFetchPost = withTokenRefresh(rawFetchPost);
const apiFetchFile = withTokenRefresh(rawFetchFile);
const apiFetchDelete = withTokenRefresh(rawFetchDelete);
const apiFetchPatch = withTokenRefresh(rawFetchPatch);
const apiFetchPut = withTokenRefresh(rawFetchPut);

const TerserahApi = () => ({
  // Generic HTTP methods
  get: async (endpoint, params = {}) => {
    const json = await apiFetchGet(endpoint, params);
    return json;
  },
  post: async (endpoint, data = {}) => {
    const json = await apiFetchPost(endpoint, data);
    return json;
  },
  patch: async (endpoint, data = {}) => {
    const json = await apiFetchPatch(endpoint, data);
    return json;
  },
  put: async (endpoint, data = {}) => {
    const json = await apiFetchPut(endpoint, data);
    return json;
  },
  delete: async (endpoint) => {
    const json = await apiFetchDelete(endpoint);
    return json;
  },
  // Descarga binaria (CSV…) con token y refresco automático
  getBlob: async (endpoint) => {
    await ensureFreshToken();
    const res = await fetch(BASEAPI + endpoint, {
      headers: { ...baseHeaders(), ...authHeader() },
    });
    if (!res.ok) {
      throw new ApiError('Request failed', res.status, null);
    }
    return res.blob();
  },
  
  // ── Session (public: must NOT go through the refresh wrapper) ──────
  login: async (identifier, password) => {
    const json = await rawFetchPost(
      '/user/signin', {
        identifier,
        password,
      },
    );
    return json;
  },
  refresh: async (refreshToken) => {
    const json = await rawFetchPost('/user/refresh', { refreshToken });
    return json;
  },
  signout: async () => {
    const json = await rawFetchPost('/user/signout', {});
    return json;
  },
  register: async (name, stateLoc, email, phone, password, password_confirmation, user_type = 'particular', honeypot = '', formStartTime = null, recaptchaToken = null) => {
    const json = await rawFetchPost(
      '/user/signup', {
        name,
        email,
        phone,
        password,
        password_confirmation,
        state: stateLoc,
        user_type,
        website: honeypot, // Honeypot field
        formStartTime, // Time-based protection
        recaptchaToken, // reCAPTCHA v3 token
      },
    );

    return json;
  },
  verifyPhoneCode: async (email, code) => {
    const json = await rawFetchPost('/user/verify-phone', { email, code });
    return json;
  },
  resendPhoneCode: async (email) => {
    const json = await rawFetchPost('/user/resend-phone-code', { email });
    return json;
  },
  completeOAuthProfile: async (token, phone, countryCode) => {
    const json = await rawFetchPost(
      '/auth/complete-profile',
      {
        phone,
        countryCode,
      },
      token,
    );

    return json;
  },
  getStates: async () => {
    const json = await rawFetchGet('/states');

    return json?.states ?? json;
  },
  getCategories: async () => {
    const json = await rawFetchGet('/categories');

    return json?.categories ?? json;
  },
  getAds: async options => {
    const json = await apiFetchGet(
      '/ad/list',
      options,
    );

    return json;
  },
  getAd: async (id, otherAds = false) => {
    const json = await apiFetchGet(
      '/ad/item',
      {
        id,
        other: otherAds,
      },
    );
    return json;
  },
  addAd: async fData => {
    const json = await apiFetchFile(
      '/ad/add',
      fData,
    );
    return json;
  },
  updateAd: async (adPublicId, fData) => {
    const json = await apiFetchFile(
      `/ad/${adPublicId}`,
      fData,
      'PUT'
    );
    return json;
  },
  getMyAds: async () => {
    const json = await apiFetchGet('/ad/my');
    return json;
  },
  deleteAd: async (adId) => {
    const json = await apiFetchDelete(`/ad/${adId}`);
    return json;
  },
  markAdAsSold: async (adId) => {
    const json = await apiFetchPost(`/ad/${adId}/sold`, {});
    return json;
  },
  unmarkAdAsSold: async (adId) => {
    const json = await apiFetchDelete(`/ad/${adId}/sold`);
    return json;
  },
  
  // Favoritos
  addFavorite: async (adPublicId) => {
    const json = await apiFetchPost('/favorites/add', { adPublicId });
    return json;
  },
  removeFavorite: async (adPublicId) => {
    const json = await apiFetchDelete(`/favorites/remove/${adPublicId}`);
    return json;
  },
  getUserFavorites: async () => {
    const json = await apiFetchGet('/favorites');
    return json;
  },
  checkFavorite: async (adPublicId) => {
    const json = await apiFetchGet(`/favorites/check/${adPublicId}`);
    return json;
  },
  
  // Mensajes (legacy - redirected to chat endpoints)
  sendMessage: async (adPublicId, message) => {
    const json = await apiFetchPost('/messages/send', { adPublicId, message });
    return json;
  },
  getConversation: async (adPublicId) => {
    const json = await apiFetchGet(`/chat/conversation/${adPublicId}`);
    return json;
  },
  getUserMessages: async () => {
    const json = await apiFetchGet('/chat/conversations');
    return json;
  },
  getUnreadCount: async () => {
    const json = await apiFetchGet('/chat/unread-count');
    return json;
  },
  
  // Chat API (new)
  getConversations: async () => {
    const json = await apiFetchGet('/chat/conversations');
    return json;
  },
  getConversationMessages: async (adPublicId, page = 1) => {
    const json = await apiFetchGet(`/chat/conversation/${adPublicId}`, { page });
    return json;
  },
  getChatUnreadCount: async () => {
    const json = await apiFetchGet('/chat/unread-count');
    return json;
  },
  
  // Offers API
  createOffer: async (adId, data) => {
    const json = await apiFetchPost(`/ads/${adId}/offers`, data);
    return json;
  },
  getAdOffers: async (adId) => {
    const json = await apiFetchGet(`/ads/${adId}/offers`);
    return json;
  },
  acceptOffer: async (offerId) => {
    const json = await apiFetchPost(`/offers/${offerId}/accept`, {});
    return json;
  },
  rejectOffer: async (offerId) => {
    const json = await apiFetchPost(`/offers/${offerId}/reject`, {});
    return json;
  },
  counterOffer: async (offerId, data) => {
    const json = await apiFetchPost(`/offers/${offerId}/counter`, data);
    return json;
  },
  getMyPendingOffers: async () => {
    const json = await apiFetchGet('/my-offers/pending');
    return json;
  },

  // =============================================================================
  // REVIEW API METHODS
  // =============================================================================

  // Create a review
  createReview: async (data) => {
    const json = await apiFetchPost('/reviews', data);
    return json;
  },

  // Get user's reviews
  getUserReviews: async (userId, page = 1, limit = 10) => {
    const json = await apiFetchGet(`/users/${userId}/reviews`, { page, limit });
    return json;
  },

  // Get user's rating summary
  getUserRating: async (userId) => {
    const json = await apiFetchGet(`/users/${userId}/rating`);
    return json;
  },

  // Check if user can review another user
  canUserReview: async (userId, adId = null) => {
    const params = adId ? { ad_id: adId } : {};
    const json = await apiFetchGet(`/users/${userId}/can-review`, params);
    return json;
  },

  // Delete a review
  deleteReview: async (reviewId) => {
    const json = await apiFetchDelete(`/reviews/${reviewId}`);
    return json;
  },
  
  // Dashboard
  getDashboard: async () => {
    const json = await apiFetchGet('/user/dashboard');
    return json;
  },

  // =============================================================================
  // ADMIN API METHODS
  // =============================================================================

  // Admin Stats
  getAdminStats: async () => {
    const json = await apiFetchGet('/admin/stats');
    return json;
  },
  getAdminUserGrowth: async (days = 30) => {
    const json = await apiFetchGet(`/admin/stats/user-growth?days=${days}`);
    return json;
  },
  getAdminAdGrowth: async (days = 30) => {
    const json = await apiFetchGet(`/admin/stats/ad-growth?days=${days}`);
    return json;
  },

  // Admin Users
  getAdminUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const json = await apiFetchGet(`/admin/users?${queryString}`);
    return json;
  },
  getAdminUserDetails: async (userId) => {
    const json = await apiFetchGet(`/admin/users/${userId}`);
    return json;
  },
  blockUser: async (userId, is_blocked) => {
    const json = await apiFetchPatch(`/admin/users/${userId}/block`, { is_blocked });
    return json;
  },
  changeUserRole: async (userId, role) => {
    const json = await apiFetchPatch(`/admin/users/${userId}/role`, { role });
    return json;
  },
  changeUserType: async (userId, user_type) => {
    const json = await apiFetchPatch(`/admin/users/${userId}/user-type`, { user_type });
    return json;
  },
  deleteAdminUser: async (userPublicId) => {
    const json = await apiFetchDelete(`/admin/user/${userPublicId}`);
    return json;
  },
  verifyUserEmail: async (userId, verified = true) => {
    const json = await apiFetchPatch(`/admin/users/${userId}/verify-email`, { verified });
    return json;
  },
  verifyUserPhone: async (userId, verified = true) => {
    const json = await apiFetchPatch(`/admin/users/${userId}/verify-phone`, { verified });
    return json;
  },
  
  // Phone Verification Settings
  getPhoneVerificationSetting: async () => {
    const json = await apiFetchGet('/settings/require-phone-verification');
    return json;
  },
  togglePhoneVerificationRequirement: async () => {
    const json = await apiFetchPost('/admin/settings/toggle-phone-verification', {});
    return json;
  },
  
  // SMS Verification Settings
  getSmsVerificationStatus: async () => {
    const json = await apiFetchGet('/admin/settings/sms-verification-status');
    return json;
  },
  toggleSmsVerification: async () => {
    const json = await apiFetchPost('/admin/settings/toggle-sms-verification', {});
    return json;
  },

  // Admin Ads
  getAdminAds: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const json = await apiFetchGet(`/admin/ads?${queryString}`);
    return json;
  },
  getPendingAds: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const json = await apiFetchGet(`/admin/ads/pending?${queryString}`);
    return json;
  },
  changeAdStatus: async (adPublicId, status, admin_notes = '') => {
    const json = await apiFetchPatch(`/admin/ads/${adPublicId}/status`, { status, admin_notes });
    return json;
  },
  toggleAdFeatured: async (adPublicId, is_featured) => {
    const json = await apiFetchPatch(`/admin/ads/${adPublicId}/featured`, { is_featured });
    return json;
  },
  deleteAdminAd: async (adPublicId) => {
    const json = await apiFetchDelete(`/admin/ads/${adPublicId}`);
    return json;
  },
  updateAdCategory: async (adPublicId, category_id) => {
    const json = await apiFetchPatch(`/admin/ads/${adPublicId}/category`, { category_id });
    return json;
  },
  updateAdminAd: async (adPublicId, data) => {
    const json = await apiFetchPatch(`/admin/ads/${adPublicId}`, data);
    return json;
  },

  // Reports
  reportAd: async (adId, reason, description) => {
    const json = await apiFetchPost(`/ad/${adId}/report`, { reason, description });
    return json;
  },
  getReports: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const json = await apiFetchGet(`/admin/reports?${queryString}`);
    return json;
  },
  updateReportStatus: async (reportId, status, admin_notes) => {
    const json = await apiFetchPatch(`/admin/reports/${reportId}/status`, { status, admin_notes });
    return json;
  },
  getReportStats: async () => {
    const json = await apiFetchGet(`/admin/reports/stats`);
    return json;
  },

  // Saved Searches
  createSavedSearch: async (name, search_params, notify_new_ads) => {
    const json = await apiFetchPost('/saved-searches', { name, search_params, notify_new_ads });
    return json;
  },
  getSavedSearches: async () => {
    const json = await apiFetchGet('/saved-searches');
    return json;
  },
  getSavedSearchAds: async (searchId, page = 1) => {
    const json = await apiFetchGet(`/saved-searches/${searchId}/ads`, { page });
    return json;
  },
  getNewAdsForSearch: async (searchId) => {
    const json = await apiFetchGet(`/saved-searches/${searchId}/new-ads`);
    return json;
  },
  updateSavedSearch: async (searchId, updates) => {
    const json = await apiFetchPatch(`/saved-searches/${searchId}`, updates);
    return json;
  },
  deleteSavedSearch: async (searchId) => {
    const json = await apiFetchDelete(`/saved-searches/${searchId}`);
    return json;
  },

  // Statistics
  trackAdView: async (adId) => {
    const json = await apiFetchPost(`/ad/${adId}/track-view`, {});
    return json;
  },
  getUserStatistics: async () => {
    const json = await apiFetchGet('/user/statistics');
    return json;
  },
  getAdminStatistics: async () => {
    const json = await apiFetchGet('/admin/statistics');
    return json;
  },
  getTrendingAds: async (limit = 10) => {
    const json = await apiFetchGet('/trending-ads', { limit });
    return json;
  },

  // =============================================================================
  // PROFILE MANAGEMENT API METHODS
  // =============================================================================

  // Get profile with verification status
  // Public user profile (stats, reviews, isFollowing)
  getUserProfile: async (userId) => {
    const json = await apiFetchGet(`/users/${userId}`);
    return json;
  },

  // Update the logged user's bio
  updateBio: async (bio) => {
    const json = await apiFetchPut('/user/bio', { bio });
    return json;
  },

  getProfile: async () => {
    const json = await apiFetchGet('/profile');
    return json;
  },

  // Update basic profile (name, state)
  updateProfile: async (data) => {
    const json = await apiFetchPut('/profile', data);
    return json;
  },

  // Update email (requires verification)
  updateEmail: async (data) => {
    const json = await apiFetchPut('/profile/email', data);
    return json;
  },

  // Update phone (requires verification)
  updatePhone: async (data) => {
    const json = await apiFetchPut('/profile/phone', data);
    return json;
  },

  // Verify phone code
  verifyPhone: async (code) => {
    const json = await apiFetchPost('/profile/phone/verify', { code });
    return json;
  },

  // Resend email verification
  resendEmailVerification: async () => {
    const json = await apiFetchPost('/profile/email/resend', {});
    return json;
  },

  // Resend phone verification
  resendPhoneVerification: async () => {
    const json = await apiFetchPost('/profile/phone/resend', {});
    return json;
  },

  // Change password
  changePassword: async (data) => {
    const json = await apiFetchPut('/profile/password', data);
    return json;
  },

  // =============================================================================
  // FOLLOW/UNFOLLOW API METHODS
  // =============================================================================

  // Follow a user
  followUser: async (userId) => {
    const json = await apiFetchPost(`/users/${userId}/follow`, {});
    return json;
  },

  // Unfollow a user
  unfollowUser: async (userId) => {
    const json = await apiFetchDelete(`/users/${userId}/follow`);
    return json;
  },

  // Check if following a user
  checkFollowStatus: async (userId) => {
    const json = await apiFetchGet(`/users/${userId}/follow/status`);
    return json;
  },

  // Get user's followers
  getFollowers: async (userId, page = 1) => {
    const json = await apiFetchGet(`/users/${userId}/followers`, { page });
    return json;
  },

  // Get users that current user is following
  getFollowing: async (userId, page = 1) => {
    const json = await apiFetchGet(`/users/${userId}/following`, { page });
    return json;
  },

  // Get follow counts
  getFollowCounts: async (userId) => {
    const json = await apiFetchGet(`/users/${userId}/follow/counts`);
    return json;
  },

  // Get feed of ads from followed users
  getFollowingFeed: async (page = 1) => {
    const json = await apiFetchGet('/following/feed', { page });
    return json;
  },

  // === SEARCH ===

  // Get search suggestions (autocomplete)
  getSearchSuggestions: async (query, limit = 10) => {
    const json = await apiFetchGet('/search/suggestions', { q: query, limit });
    return json;
  },

  // Get popular searches
  getPopularSearches: async (limit = 10) => {
    const json = await apiFetchGet('/search/popular', { limit });
    return json;
  },

  // Get related search terms
  getRelatedSearchTerms: async (query, limit = 8) => {
    const json = await apiFetchGet('/search/related-terms', { q: query, limit });
    return json;
  },
});

export default TerserahApi;